import React, { useState, useEffect } from "react";
import DataTable from "react-data-table-component";
import { FaEdit, FaTrash } from "react-icons/fa";
import config from "../../../config/config";
import { getAuthToken } from "../../../utils/token";
import axios from "axios";
import { toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import Loader from "../../loader/Loader";

const customStyles = {
  headCells: {
    style: {
      backgroundColor: "#0d6efd",
      color: "white",
      fontWeight: "bold",
      fontSize: "14px",
    },
  },
};

export default function Training() {
  const [dataList, setDataList] = useState([]);
  const [filterText, setFilterText] = useState("");
  const [showModal, setShowModal] = useState(false);
  const [isEditing, setIsEditing] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [listLoading, setListLoading] = useState(false);
  const [formLoading, setFormLoading] = useState(false);
  const [form, setForm] = useState({ name: "", description: "" });

  useEffect(() => {
    fetchList();
  }, []);

  const fetchList = async () => {
    const token = getAuthToken();
    if (!token) return;
    setListLoading(true);
    try {
      const res = await axios.get(`${config.API}/admin/traininglist`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setDataList(res.data.trainings || []);
    } catch (err) {
      console.error("Error fetching training list:", err);
      toast.error("Failed to load data");
    } finally {
      setListLoading(false);
    }
  };

  const handleInput = (e) => {
    const { name, value } = e.target;
    setForm((prev) => ({ ...prev, [name]: value }));
  };

  const openAddModal = () => {
    setForm({ name: "", description: "" });
    setIsEditing(false);
    setEditingId(null);
    setShowModal(true);
  };

  const openEditModal = (item) => {
    setForm({ name: item.name || "", description: item.description || "" });
    setIsEditing(true);
    setEditingId(item.id);
    setShowModal(true);
  };

 const handleSubmit = async () => {
  const token = getAuthToken();
  if (!token) {
    toast.error("Authentication missing");
    return;
  }

  setFormLoading(true);

  try {
    let response;

    if (isEditing && editingId) {
      response = await axios.put(
        `${config.API}/admin/updatetraining/${editingId}`,
        form,
        { headers: { Authorization: `Bearer ${token}` } }
      );
    } else {
      response = await axios.post(
        `${config.API}/admin/createtraning`,
        form,
        { headers: { Authorization: `Bearer ${token}` } }
      );
    }
    if (response?.data?.message) {
      toast.success(response.data.message);
    } else {
      toast.success("Submitted successfully");
    }

    setShowModal(false);
    fetchList();
  } catch (err) {
    const backendMsg =
      err.response?.data?.message ||
      err.response?.data?.error ||
      "Submit failed! Please try again.";

    toast.error(backendMsg);
  } finally {
    setFormLoading(false);
  }
};

  const handleDelete = async (id) => {
    const token = getAuthToken();
    if (!token) return;
    if (!window.confirm("Are you sure you want to delete this training?")) return;
    try {
      await axios.delete(`${config.API}/admin/deletetraining/${id}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      toast.success("Deleted successfully");
      fetchList();
    } catch (err) {
      console.error("Delete error:", err);
      toast.error("Failed to delete");
    }
  };
  const columns = [
    { name: "ID", selector: (row) => row.id, sortable: true, width: "70px" },
    { name: "Name", selector: (row) => row.name, sortable: true },
    { name: "Description", selector: (row) => row.description, sortable: true },
    {
      name: "Actions",
      cell: (row) => (
        <div className="d-flex gap-2">
          <button
            className="btn btn-sm btn-primary"
            onClick={() => openEditModal(row)}
          >
            <FaEdit />
          </button>
          <button
            className="btn btn-sm btn-danger"
            onClick={() => handleDelete(row.id)}
          >
            <FaTrash />
          </button>
        </div>
      ),
      ignoreRowClick: true,
      allowOverflow: true,
      button: true,
    },
  ];

  return (
    <div className="container mt-3">
      <div className="d-flex justify-content-between align-items-center mb-3">
        <strong className="mb-0 fs-4">Training List</strong>
        <div>
          <input
            type="text"
            className="form-control d-inline-block me-2"
            placeholder="Search..."
            value={filterText}
            onChange={(e) => setFilterText(e.target.value)}
            style={{ width: "200px" }}
          />
          <button className="btn btn-primary" onClick={openAddModal}>
            + Add Training
          </button>
        </div>
      </div>

      {listLoading ? (
        <div className="text-center">
          <Loader />
        </div>
      ) : (
        <DataTable
          columns={columns}
          data={dataList.filter((item) =>
            Object.values(item).join(" ").toLowerCase().includes(filterText.toLowerCase())
          )}
          pagination
          highlightOnHover
          customStyles={customStyles}
        />
      )}

      {showModal && (
        <div className="modal d-block" style={{ background: "rgba(0,0,0,0.5)" }}>
          <div className="modal-dialog modal-md">
            <div className="modal-content">
              <div className="modal-header bg-primary text-light">
                <h5>{isEditing ? "Edit Training" : "Add Training"}</h5>
                <button
                  className="btn-close btn-close-white"
                  onClick={() => setShowModal(false)}
                />
              </div>

              <div className="modal-body">
                <div className="mb-3">
                  <label className="form-label">Name</label>
                  <input
                    type="text"
                    name="name"
                    className="form-control"
                    value={form.name}
                    onChange={handleInput}
                    disabled={formLoading}
                  />
                </div>

                <div className="mb-3">
                  <label className="form-label">Description</label>
                  <textarea
                    name="description"
                    className="form-control"
                    value={form.description}
                    onChange={handleInput}
                    disabled={formLoading}
                  ></textarea>
                </div>
              </div>

              <div className="modal-footer">
                <button
                  className="btn btn-secondary"
                  onClick={() => setShowModal(false)}
                  disabled={formLoading}
                >
                  Cancel
                </button>
                <button
                  className="btn btn-primary"
                  onClick={handleSubmit}
                  disabled={formLoading}
                >
                  {formLoading ? "Saving..." : isEditing ? "Update" : "Save"}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
